import { useCallback, useMemo, useState } from 'react';
import { DATA_ENTITY_TYPE } from './const';
import { useTextWithUIConversation } from './conversation/use-text-with-ui-conversation';
import { useTextWithUIImagePreview } from './image-preview/use-text-with-ui-image-preview';
import { useTextWithUIMentions } from './mentions/use-text-with-ui-mentions';
import { UIReplacer, UIReplacerHandlers, UIReplacerName } from './types';

export const useTextWithUI = (
  originalText: string,
  metaData?: Record<UIReplacerName, Record<string, unknown[]>>,
) => {
  const textUIConversation = useTextWithUIConversation();
  const textWithUIImagePreview = useTextWithUIImagePreview();
  const textWithUIMentions = useTextWithUIMentions();

  const entities: UIReplacer[] = useMemo(
    () => [textUIConversation, textWithUIImagePreview, textWithUIMentions],
    [textUIConversation, textWithUIImagePreview, textWithUIMentions],
  );

  const handlersMap = entities.reduce<Record<UIReplacerName, UIReplacerHandlers>>(
    (acc, it) => ({ ...acc, [it.name]: it.handlers || {} }),
    {} as Record<UIReplacerName, UIReplacerHandlers>,
  );

  const handlersTypes = Array.from(
    new Set(Object.values(handlersMap).flatMap((handler) => Object.keys(handler))),
  );

  const text = entities.reduce((acc, it) => {
    let result = acc;
    if (it.replace) {
      try {
        result = it.replace(acc, metaData?.[it.name] || {});
      } catch (e) {
        console.error(`Error replacing text for entity ${it.name}:`, e);
      }
    }
    return result;
  }, originalText);

  const eventHandler = useCallback(
    (eventHandlerName: string): React.MouseEventHandler<HTMLElement> =>
      (e) => {
        const target = e.target as HTMLElement;
        let entityType;
        let entityElement = target.closest(`[${DATA_ENTITY_TYPE}]`);

        if (entityElement) {
          entityType = entityElement.getAttribute(DATA_ENTITY_TYPE);
        } else {
          entityElement = target.closest('img');
          entityType = UIReplacerName.ImagePreview;
        }

        if (entityElement && entityType) {
          if (entityType && entityType in handlersMap) {
            handlersMap[entityType as UIReplacerName][eventHandlerName](
              entityElement as HTMLElement,
              e,
            );
            return;
          }
        }
      },
    [handlersMap],
  );

  const handlers = useMemo<
    Partial<
      Record<
        keyof React.DOMAttributes<HTMLElement>,
        (e: Parameters<React.MouseEventHandler<HTMLElement>>[0]) => void
      >
    >
  >(() => {
    return handlersTypes.reduce(
      (acc, eventName) => ({ ...acc, [eventName]: eventHandler(eventName) }),
      {},
    );
  }, [eventHandler, handlersTypes]);

  const parentStyles = useMemo(() => {
    return entities.reduce<Record<string, unknown>>(
      (acc, it) => ({ ...acc, ...it.parentStyles }),
      {},
    );
  }, [entities]);

  return useMemo(
    () => ({
      text,
      handlers,
      parentStyles,
    }),
    [handlers, text, parentStyles],
  );
};

export const useTextWithUIPrepareMetaData = () => {
  const textUIConversation = useTextWithUIConversation();
  const entities: UIReplacer[] = useMemo(
    () => [
      textUIConversation,
      // ...other entities with metadata loading can be added here in the future
    ],
    [textUIConversation],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [metaData, setMetaData] =
    useState<Record<UIReplacerName, Record<string, unknown[]>>>();

  const loadMetadata = useCallback(
    async (input: string) => {
      setIsLoading(true);
      const metaDataPromises = entities.map((entity) =>
        entity.loadMetaData ? entity.loadMetaData(input) : Promise.resolve({}),
      );

      try {
        try {
          const results = await Promise.all(metaDataPromises);

          setMetaData((prevMetaData) => {
            return entities.reduce(
              (acc, it, index) => {
                const entityName = it.name;
                const newData = results[index] || {};

                // Merge previous and new data for this entity
                acc[entityName] = {
                  ...(prevMetaData?.[entityName] || {}), // Previous data
                  ...newData, // New data (will override duplicate keys)
                };

                return acc;
              },
              {} as Record<UIReplacerName, Record<string, unknown[]>>,
            );
          });
        } catch (error) {
          console.error(`Error fetching messages metadata for entity:`, error);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [entities],
  );

  return useMemo(
    () => ({
      metaData,
      isLoading,
      loadMetadata,
    }),
    [metaData, isLoading, loadMetadata],
  );
};
